import { zodResolver } from "@hookform/resolvers/zod";
import classNames from "classnames";
import React, { useCallback, useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useLocation, useNavigate } from "react-router-dom";
import { setLoginRes } from "../../api";
import {
  generateResetPasswordCode,
  resetPasswordInput,
  resetPasswordSchema,
  setNewPasswordAPI,
  verifyVerificationCode,
} from "../../services/user";
import Button from "../Common/Buttons/Button";
import InputField from "../Common/Input/InputField";
import PasswordInputField from "../Common/Input/PasswordInputField";
import { customToast } from "../Common/Toast";

const ResetPasswordForm: React.FC = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState<number>(1);
  const [username, setUsername] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [userAccountID, setUserAccountID] = useState<string | null>(null);
  const [isVerificationLoading, setIsVerificationLoading] =
    useState<boolean>(false);
  const [isResettingPassword, setIsResettingPassword] =
    useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();

  const methods = useForm<resetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
    mode: "onChange",
  });

  const { handleSubmit, formState } = methods;

  const handleCodeVerification = useCallback(async () => {
    if (!code) return;

    setStatus("loading");
    try {
      const response = await verifyVerificationCode(code);

      if (response.status === 200) {
        if (response.data === "Record Not found into database") {
          setStatus("error");
          //toast.error("Code verification failed. Please try again.");
          customToast(t("auth.reset_password.verification_failed"), "error");
        } else {
          //toast.success("Code verification successful");
          customToast(t("auth.reset_password.verification_success"), "success");
          setLoginRes(response.data.item1);
          setUserAccountID(response.data.item2?.userAccountID ?? null);
          setStatus("success");
        }
      }
    } catch {
      setStatus("error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const resetCode = searchParams.get("PasswordResetVerificationCode");

    if (resetCode) {
      setStep(2);
      setCode(resetCode);
      handleCodeVerification();
    }
  }, [location.search, handleCodeVerification]);

  const handleUsernameCheck = async (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();
    setMessage("");
    setIsVerificationLoading(true);

    try {
      await generateResetPasswordCode(username);

      setUsername("");
      setMessage(t("auth.reset_password.code_sent_success"));
    } catch {
      setMessage(t("auth.reset_password.code_sent_error"));
    } finally {
      setIsVerificationLoading(false);
    }
  };

  const handleResetPassword = async (data: resetPasswordInput) => {
    if (!userAccountID) return;

    setMessage("");
    setIsResettingPassword(true);

    try {
      const searchParams = new URLSearchParams(location.search);
      const resetCode = searchParams.get("PasswordResetVerificationCode");
      if (!resetCode) {
        setMessage(t("auth.reset_password.invalid_reset_code"));
        setIsResettingPassword(false);
        return;
      }
      await setNewPasswordAPI(data.password, userAccountID, resetCode);
      navigate("/wizard");
    } catch {
      setMessage(t("auth.reset_password.reset_failed"));
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleRetryPasswordReset = () => {
    navigate("/forgot-password");
    window.location.reload();
  };

  return (
    <>
      {step === 1 ? (
        <>
          <div className="font-outfit max-w-4xl flex justify-center md:mt-[40px]">
            <div className="md:shadow-lg w-[420px] md:bg-white rounded-[24px] md:p-[32px]">
              <h1 className="block font-semibold text-[32px] leading-[127%] ">
                {t("auth.reset_password.title")}
              </h1>
              <p className="py-[16px] text-[18px] leading-[127%]">
                {t("auth.reset_password.description")}
              </p>
              {message && <p className="text-green-500 mb-4">{message}</p>}
              <form>
                <div className="mb-4">
                  <div className="relative flex flex-col space-y-[16px] text-[18px] mb-[32px]">
                    <InputField
                      type="text"
                      id="username"
                      name="username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      label={t("auth.reset_password.email_label")}
                      placeholder={t("auth.reset_password.email_placeholder")}
                      autoComplete="username"
                    />
                  </div>
                </div>

                <Button
                  iconVisibility="hide"
                  ariaLabel="login"
                  label={t("auth.reset_password.send_button")}
                  loading={isVerificationLoading}
                  handleClick={handleUsernameCheck}
                />
              </form>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="font-outfit w-full md:w-[420px] md:mt-[40px]">
            <div className="md:shadow-lg bg-white rounded-[24px] md:p-[32px]">
              <h1 className="block font-semibold text-[32px] leading-[127%] ">
                {t("auth.reset_password.step2_title")}
              </h1>
              <p className="py-[16px] text-[18px] leading-[127%]">
                {t("auth.reset_password.step2_description")}
              </p>
              {message && <p className="text-green-500 mb-4">{message}</p>}
              {status === "loading" && (
                <p className="text-blue-500 mb-4">
                  {t("auth.reset_password.verifying_code")}
                </p>
              )}
              {status === "error" && (
                <>
                  <p className="text-red-500 mb-4">
                    {t("auth.reset_password.verification_failed")}
                  </p>
                  <button
                    type="button"
                    className="btn btn-primary mt-4"
                    onClick={handleRetryPasswordReset}
                  >
                    {t("auth.reset_password.retry_button")}
                  </button>
                </>
              )}
              {status === "success" && (
                <FormProvider {...methods}>
                  <form onSubmit={handleSubmit(handleResetPassword)}>
                    <div className="mb-4">
                      <div className="relative flex flex-col space-y-[16px] text-[18px] mb-[32px]">
                        <Controller
                          control={methods.control}
                          name="password"
                          render={({ field: { value, onChange } }) => (
                            <PasswordInputField
                              id="password"
                              type="password"
                              name="password"
                              value={value}
                              onChange={onChange}
                              label="Password"
                              placeholder="Password"
                              isNewPassword={true}
                            />
                          )}
                        />
                        <Controller
                          control={methods.control}
                          name="confirmPassword"
                          render={({ field: { value, onChange } }) => (
                            <PasswordInputField
                              id="confirmPassword"
                              type="password"
                              name="confirmPassword"
                              value={value}
                              onChange={onChange}
                              label="Password"
                              placeholder="Confirm New Password"
                              isNewPassword={false}
                            />
                          )}
                        />

                        {/* <InputField
                          id="password"
                          type="password"
                          name="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          label="New Password"
                          placeholder="New Password"
                        />

                        <InputField
                          id="confirmPassword"
                          type="password"
                          name="confirmPassword"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          label="Confirm Password"
                          placeholder="Confirm New Password"
                        /> */}
                      </div>
                    </div>

                    <button
                      type="submit"
                      className={classNames(
                        "btn btn-primary",
                        !formState.isValid && "opacity-50 cursor-not-allowed",
                      )}
                      disabled={isResettingPassword}
                    >
                      {isResettingPassword
                        ? t("auth.reset_password.resetting")
                        : t("auth.reset_password.reset_button")}
                    </button>
                  </form>
                </FormProvider>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ResetPasswordForm;
